import { useCallback, useEffect, useRef, useState } from "react";
import type { ClientMsg, PublicState, ServerMsg } from "../../shared/types.ts";

export interface MeInfo {
  id: string; name: string; rig: string | null; demo: boolean;
  others: { id: string; name: string; rig: string | null }[];
  iceServers: RTCIceServer[];
  voice: boolean; turn: boolean; cardLink: string;
}

export async function fetchMe(): Promise<MeInfo | null> {
  const r = await fetch("/api/me");
  if (!r.ok) return null;
  return r.json();
}

export function useSocket(device: "room" | "card", onMessage?: (m: ServerMsg) => void) {
  const [state, setState] = useState<PublicState | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ws = useRef<WebSocket | null>(null);
  const handler = useRef(onMessage);
  handler.current = onMessage;

  useEffect(() => {
    let closed = false;
    let retry: ReturnType<typeof setTimeout>;
    let ping: ReturnType<typeof setInterval>;
    const open = () => {
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const s = new WebSocket(`${proto}://${location.host}/ws?device=${device}`);
      ws.current = s;
      s.onopen = () => {
        setConnected(true);
        ping = setInterval(() => { if (s.readyState === 1) s.send(JSON.stringify({ t: "ping" })); }, 25000);
      };
      s.onmessage = (e) => {
        const m = JSON.parse(e.data) as ServerMsg;
        if (m.t === "state") setState(m.state);
        if (m.t === "error") { setError(m.message); setTimeout(() => setError(null), 5000); }
        handler.current?.(m);
      };
      s.onclose = () => {
        setConnected(false);
        clearInterval(ping);
        if (!closed) retry = setTimeout(open, 1000);
      };
    };
    open();
    return () => { closed = true; clearTimeout(retry); clearInterval(ping); ws.current?.close(); };
  }, [device]);

  const send = useCallback((m: ClientMsg) => {
    if (ws.current?.readyState === 1) ws.current.send(JSON.stringify(m));
  }, []);

  return { state, connected, error, send };
}
